import axios from "axios";
import { adminApi } from "../../lib/axios";

// Agency payment method field interface
export interface AgencyPaymentMethodField {
  id: string;
  fieldName: string;
  label: string;
  isRequired: boolean;
}

// Agency payment method interface
export interface AgencyPaymentMethod {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  fields: AgencyPaymentMethodField[];
}

// Agency payment info interface
export interface AgencyPaymentInfo {
  id: string;
  paymentMethod: AgencyPaymentMethod;
  fieldData: Record<string, string>;
}

// Agency info interface
export interface PayoutAgency {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

// Agency payout request interface
export interface AgencyPayoutRequest {
  id: string;
  amount: number;
  status: "PENDING" | "PAID" | "REJECTED";
  createdAt: string;
  paidAt?: string | null;
  referenceNumber?: string | null;
  paymentDetails?: {
    paymentMethodId?: string;
    paymentMethodName?: string;
    fieldData?: Record<string, string>;
  } | null;
  agency: PayoutAgency;
  paymentInfos: AgencyPaymentInfo[];
}

// Service class
export interface AgencyPayoutPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface AgencyPayoutSearchResponse {
  data: AgencyPayoutRequest[];
  pagination: AgencyPayoutPagination;
}

export class AdminAgencyPayoutsService {
  // Get agency payout requests (paginated)
  async getAgencyPayoutRequests(page: number = 1, limit: number = 10, status: 'PENDING' | 'PAID' | 'REJECTED' = 'PENDING'): Promise<AgencyPayoutSearchResponse> {
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', String(limit));
      params.append('status', status);
      const response = await adminApi.get(`/agency/admin/payouts?${params.toString()}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch agency payout requests"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Mark agency payout as paid
  async markAgencyPayoutPaid(
    payoutId: string,
    paymentMethodRecordId: string,
    referenceNumber?: string
  ): Promise<void> {
    try {
      await adminApi.patch(`/agency/admin/payouts/${payoutId}/paid`, {
        paymentMethodRecordId,
        referenceNumber,
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to mark agency payout as paid"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Stats
  async getAgencyPayoutStats(): Promise<{
    totalPendingRequests: number;
    totalPaidRequests: number;
    totalRequests: number;
    totalPendingAmount: number;
    totalPaidAmount: number;
    totalAmount: number;
    averagePayoutPerRequest: number;
  }> {
    try {
      const response = await adminApi.get('/agency/admin/payouts/overview');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch agency payout stats"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Helper method to format agency payout data for display
  formatAgencyPayoutForDisplay(payout: AgencyPayoutRequest) {
    const agencyName = `${payout.agency.firstName} ${payout.agency.lastName}`;

    return {
      id: payout.id,
      amount: payout.amount,
      status: payout.status,
      agencyName,
      agencyEmail: payout.agency.email,
      createdAt: new Date(payout.createdAt).toLocaleDateString(),
      hasPaymentMethods: payout.paymentInfos.length > 0,
      paymentInfos: payout.paymentInfos,
    };
  }
}

// Export singleton instance
export const adminAgencyPayoutsService = new AdminAgencyPayoutsService();